import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PublicQuoteView.css';

// Create a separate axios instance for public routes
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

const PublicQuoteView = () => {
  const { quoteNumber } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');

  useEffect(() => {
    fetchQuote();
  }, [quoteNumber]);

  const fetchQuote = async () => {
    try {
      const response = await publicAxios.get(`/public/quotes/${quoteNumber}`);
      setQuote(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading quote');
      setLoading(false);
    }
  };

  const handlePayDeposit = async () => {
    try {
      const response = await publicAxios.post(`/public/quotes/${quoteNumber}/payment`, {
        paymentType: 'deposit'
      });
      
      if (response.data.success) {
        // Navigate to payment page
        navigate(response.data.paymentUrl);
      }
    } catch (err) {
      alert('Error initiating payment. Please try again.');
    }
  };

  const handleContactAgent = () => {
    setShowContactModal(true);
  };

  const sendContactMessage = async () => {
    try {
      await publicAxios.post(`/public/quotes/${quoteNumber}/contact`, {
        message: contactMessage
      });
      alert('Your message has been sent to the agent.');
      setShowContactModal(false);
      setContactMessage('');
    } catch (err) {
      alert('Error sending message. Please try again.');
    }
  };

  const downloadAttachment = (fileId, fileName) => {
    window.open(`/api/public/files/${fileId}/download`, '_blank');
  };

  if (loading) return <div className="loading">Loading quote...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!quote) return <div className="error">Quote not found</div>;

  const leadTraveler = quote.lead_traveler || {};
  const agencyInfo = quote.agency_info || {};
  const depositAmount = quote.deposit_amount || (quote.total_amount * 0.3);

  return (
    <div className="public-quote-view">
      <div className="quote-header">
        <div className="agency-branding">
          {agencyInfo.logo && (
            <img 
              src={`/uploads/${agencyInfo.logo}`} 
              alt={agencyInfo.name || quote.agency_name} 
              className="agency-logo"
            />
          )}
          <div className="agency-details">
            <h3>{agencyInfo.name || quote.agency_name}</h3>
            <p className="agent-name">{quote.agent_name}</p>
          </div>
        </div>
        <div className="quote-info">
          <h1>{quote.title}</h1>
          <p className="quote-number">Quote #{quote.quote_number}</p>
          <p className="quote-date">Created: {new Date(quote.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      <div className="quote-content">
        <div className="traveler-info">
          <h2>Traveler Information</h2>
          <p><strong>Lead Traveler:</strong> {leadTraveler.firstName} {leadTraveler.lastName}</p>
          <p><strong>Email:</strong> {leadTraveler.email}</p>
          <p><strong>Phone:</strong> {leadTraveler.phone}</p>
          {quote.additional_members && quote.additional_members.length > 0 && (
            <div className="additional-travelers">
              <p><strong>Additional Travelers:</strong></p>
              <ul>
                {quote.additional_members.map((member, index) => (
                  <li key={index}>{member.firstName} {member.lastName}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {quote.options && quote.options.length > 0 ? (
          <div className="quote-options">
            <h2>Package Options</h2>
            {quote.options.map((option, index) => (
              <div key={option.id} className="option-card">
                <div className="option-header">
                  <h3>Option {String.fromCharCode(65 + index)}: {option.name}</h3>
                  <div className="option-price">${parseFloat(option.totalPrice || 0).toFixed(2)}</div>
                </div>
                <p className="option-description">{option.description}</p>
                <div className="option-components">
                  {option.components.map((component) => (
                    <div key={component.id} className="component-item">
                      <span className="component-type">{component.type}</span>
                      <span className="component-title">{component.title}</span>
                      <span className="component-price">${parseFloat(component.price || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="quote-components">
            <h2>Travel Components</h2>
            {quote.components && quote.components.map((component) => (
              <div key={component.id} className="component-card">
                <div className="component-header">
                  <h3>{component.title}</h3>
                  <span className="component-price">${parseFloat(component.price || 0).toFixed(2)}</span>
                </div>
                <p className="component-type">{component.type}</p>
                {component.details && (
                  <div className="component-details">
                    {Object.entries(component.details).map(([key, value]) => (
                      <p key={key}><strong>{key}:</strong> {value}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {quote.attachments && quote.attachments.length > 0 && (
          <div className="quote-attachments">
            <h2>Attachments</h2>
            <div className="attachments-list">
              {quote.attachments.map((file) => (
                <div key={file.id} className="attachment-item">
                  <span className="file-icon">📎</span>
                  <span className="file-name">{file.file_name}</span>
                  <button 
                    className="download-btn"
                    onClick={() => downloadAttachment(file.id, file.file_name)}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="quote-summary">
          <div className="pricing-summary">
            <div className="price-line">
              <span>Total Amount:</span>
              <span className="total-amount">${parseFloat(quote.total_amount || 0).toFixed(2)}</span>
            </div>
            <div className="price-line deposit">
              <span>Deposit Required:</span>
              <span className="deposit-amount">${parseFloat(depositAmount).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {quote.notes && (
          <div className="quote-notes">
            <h2>Additional Information</h2>
            <p>{quote.notes}</p>
          </div>
        )}
      </div>

      <div className="quote-footer">
        <div className="agent-info">
          <h3>Your Travel Agent</h3>
          <p><strong>{quote.agent_name}</strong></p>
          <p>{agencyInfo.name || quote.agency_name}</p>
          <p>📞 {agencyInfo.phone || quote.agent_phone}</p>
          <p>✉️ {agencyInfo.email || quote.agent_email}</p>
          <div className="social-links">
            {agencyInfo.facebook && (
              <a href={agencyInfo.facebook} target="_blank" rel="noopener noreferrer">Facebook</a>
            )}
            {agencyInfo.instagram && (
              <a href={agencyInfo.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>
            )}
            {agencyInfo.linkedin && (
              <a href={agencyInfo.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
            )}
          </div>
        </div>
        
        <div className="action-buttons">
          <button className="btn-secondary" onClick={handleContactAgent}>
            Request a Change
          </button>
          <button className="btn-primary" onClick={handlePayDeposit}>
            Pay Deposit (${parseFloat(depositAmount).toFixed(2)})
          </button>
        </div>
      </div>

      {showContactModal && (
        <div className="modal-overlay">
          <div className="contact-modal">
            <h2>Request a Change</h2>
            <textarea
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              placeholder="Please describe the changes you'd like to make to your quote..."
              rows={6}
            />
            <div className="modal-buttons">
              <button onClick={() => setShowContactModal(false)}>Cancel</button>
              <button onClick={sendContactMessage} className="btn-primary">Send Message</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicQuoteView;
